import { parseYamlTasks } from '@/backend/helpers/parseYamlTasks';
import { Commanding } from '@/backend/models/commanding';
import { CommandingRun } from '@/backend/models/commandingRun';
import { useTreeStore } from '@/frontend/store/TreeStore';
import { I18n } from '@/i18n/I18n';
import { CommandingRunStages, RUN_STATE } from '@/types/commandingRunTaskTypes';
import { Operation } from '@/backend/models/operation';
import { useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { ButtonParent } from '../buttons/ButtonParent';
import { Bug } from '@/frontend/SVGs/Bug';
import { TaskRow } from './TaskRow';
import { resolveTaskAlias, resolvePersistedTaskAlias } from './resolveTaskAlias';
import { useEquipmentStore } from '@/frontend/store/EquipmentStore';
import { CommandingRunDisplayItemWrapper } from './CommandingRunDisplayItemWrapper';
import { CommandingDebugger } from './CommandingDebugger';
import { RunAutomationControl } from './RunAutomationControl';


export type Task = {
  alias: string;
  state: RUN_STATE;
  tooltip: string;
  startTime: Date | string | undefined;
  fields?: string;
};

export function CommandingRunBody(props: {
  operation: Operation;
}) {
  const { operation } = props;
  const commanding = operation.commanding as Commanding;
  const commandingRun = useTreeStore(useShallow((state) => state.commandingRuns.find((run: CommandingRun) => run.commandingId === commanding.id)));
  const runTasks = useTreeStore(useShallow((state) => state.runTasks.filter((task) => task.commandingRunId === commandingRun?.id)));
  const equipmentList = useEquipmentStore((state) => state.equipmentList);
  let inputTasks = [];
  let outputTasks = [];

  if (commandingRun) {
    inputTasks = runTasks.filter((task) => task.stage === CommandingRunStages.INPUT)
    .map((task) => { const parsed = parseYamlTasks(task.taskResponseYaml)[0]; return { alias: resolvePersistedTaskAlias(task.alias, equipmentList), state: task.runState, tooltip: task.statusMessage, startTime: task.startTime, fields: parsed?.taskValues }; });
    outputTasks = runTasks.filter((task) => task.stage === CommandingRunStages.OUTPUT)
    .map((task) => { return { alias: resolvePersistedTaskAlias(task.alias, equipmentList), state: task.runState, tooltip: task.statusMessage, startTime: task.startTime }; });
  } else {
    inputTasks = parseYamlTasks(commanding.inputTasks || '').map((task) => { return { alias: resolveTaskAlias(task.name, task, equipmentList), state: RUN_STATE.EMPTY, tooltip: '', startTime: undefined, fields: task.taskValues }; });
    outputTasks = parseYamlTasks(commanding.outputTasks || '').map((task) => { return { alias: resolveTaskAlias(task.name, task, equipmentList), state: RUN_STATE.EMPTY, tooltip: '', startTime: undefined }; });
  }

  const [showDebug, setShowDebug] = useState(false);


  return (
    <div className="row mt-2">
      <div className="col border-2">
        <div className='card bg-light border-success-subtle'>
          <div className="card-header">
            <div className="d-flex justify-content-between">
              <div>
                <small>{I18n.get('commandingRun')}</small>
                { commandingRun &&
                  <small className="text-muted ms-2">
                    <small>({I18n.get('updated')} { commandingRun.updatedAt?.toString() })</small>
                  </small>
                }
              </div>
              <div className="d-flex gap-1">
                <ButtonParent
                  className="btn btn-sm btn-warning d-print-none"
                  title="Debug"
                  onClick={() => setShowDebug(!showDebug)}
                >
                  <Bug />
                </ButtonParent>
              </div>
            </div>
          </div>
          <div className="card-body">
            <div className='d-flex flex-column'>
              <div className='d-flex gap-3 justify-content-between align-items-end'>
                <div className='d-flex gap-3'>
                  <div className='d-flex flex-column'>
                    <h6>{I18n.get('inputTasks')}</h6>
                    { inputTasks.map((task, index) => <TaskRow key={index} task={task} operation={operation} />) }
                  </div>
                  <div className='d-flex flex-column'>
                    <h6>{I18n.get('outputTasks')}</h6>
                    { outputTasks.map((task, index) => <TaskRow key={index} task={task} operation={operation} />) }
                  </div>
                </div>
              </div>
              { commandingRun && runTasks.filter(task => task.stage === CommandingRunStages.OUTPUT).map(task => <CommandingRunDisplayItemWrapper key={task.id} task={task} /> ) }
            </div>
            <RunAutomationControl operation={operation} hasRun={Boolean(commandingRun)} />
          </div>
          { showDebug && <div className="card-footer">
            <CommandingDebugger commandingRun={commandingRun} commanding={commanding} />
          </div>}
        </div>
      </div>
    </div>
  );
}
